<template>
  <div class="login d-flex justify-center align-center h-100 pa-5">
    <div class="login__background" :style="backgroundStyle"></div>

    <div
      :class="[
        'login__overlay',
        'login__overlay__top',
        { 'no-bg-image': !backgroundStyle }
      ]"
    ></div>

    <div
      :class="[
        'login__overlay',
        'login__overlay__bottom',
        { 'no-bg-image': !backgroundStyle }
      ]"
    ></div>

    <v-card class="pa-4" elevation="16" rounded="xl">
      <NavLogo
        alt="LOGO"
        class="mx-auto my-4 justify-center"
        :loading="false"
        :logo="logo"
        :maxHeight="40"
        :maxWidth="160"
      />

      <h4 class="text-center mb-2 mt-8">Pass Login</h4>

      <p class="text-body-2 mb-4 text-center" style="max-width: 280px;">
        Please enter the email address associated with your pass.
      </p>

      <v-form v-model="validForm" @submit.prevent="login" >
        <v-text-field
          v-model="email"
          class="rounded-xl login__text-field"
          density="compact"
          hide-details="auto"
          label="Email"
          variant="outlined"
          :rules="rules"
          type="email"
        />

        <span v-if="err" class="text-caption text-error">
          {{ err }}
        </span>

        <v-btn
          class="mt-5 login__btn"
          color="primary"
          :disabled="!validForm"
          size="large"
          height="40"
          :loading="loading"
          rounded
          type="submit"
          block
        >
          Access Your Pass
        </v-btn>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import NavLogo from '@/components/shared/NavLogo/index.vue';

export default {
  name: 'Login',
  components: {
    NavLogo
  },
  data() {
    return {
      email: '',
      err: '',
      loading: false,
      validForm: false,
      rules: [
        value => !!value || 'Required.',
        value => (value && value.length >= 3) || 'Min 3 characters',
        value => /^[^@]+@[^@]{2,}\.[^@]{2,}$/.test(value) || 'Invalid email'
      ]
    };
  },
  computed: {
    offer() {
      return this.$store.state.offer || {};
    },
    partner() {
      return this.$store.state.partner || {};
    },
    offerLogoIdx() {
      const media = this.offer.media ?? [];
      const idx = media.findIndex(
        m => m.location === 'HorizontalLogo' && m.url
      );
      return idx >= 0 ? idx : -1;
    },
    partnerLogoIdx() {
      const media = this.partner.media ?? [];
      const idx = media.findIndex(
        m => m.location === 'HorizontalLogo' && m.url
      );
      return idx >= 0 ? idx : -1;
    },
    logo() {
      if (this.offerLogoIdx < 0 && this.partnerLogoIdx < 0) {
        return undefined;
      }

      return this.offerLogoIdx >= 0
        ? this.offer.media[this.offerLogoIdx]
        : this.partner.media[this.partnerLogoIdx];
    },
    backgroundStyle() {
      // Offer login image
      let idx = this.offer?.media?.findIndex(m => m.location === 'Login');
      if (idx >= 0 && this.offer.media[idx]?.url) {
        return {
          backgroundImage: `url(${this.offer.media[idx].url})`
        };
      }

      // Partner login image
      idx = this.partner?.media?.findIndex(m => m.location === 'Login');
      if (idx >= 0 && this.partner.media[idx]?.url) {
        return {
          backgroundImage: `url(${this.partner.media[idx].url})`
        };
      }

      return null;
    }
  },
  methods: {
    async login() {
      try {
        this.err = '';
        this.loading = true;

        const { data } = await this.$api.auth.login({ email: this.email });

        await this.$store.dispatch(
          'auth/setValidated',
          { validated: data?.validated === true },
          { root: true }
        );

        await this.$store.dispatch('initialData/getInitialData', { force: true });

        this.$router.push(`/experiences?t=${window.token}`);
      } catch (error) {
        this.loading = false;
        this.err = 'We could not find this email in our logs.';
        console.error('Failed to log in.', error);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.login {
  background-position: center center;
  background-size: cover;

  &__background,
  &__overlay {
    position: fixed;
    max-width: 480px !important;
    margin: 0 auto;
    inset: 0;
    width: 100%;
    height: 100%;
    background-position: center center;
    background-size: cover;
  }

  &__overlay {
    &__top {
      background: linear-gradient(180deg, clothoid-gradient(#000000));
      height: 50%;
      opacity: 0.3;

      &.no-bg-image {
        background: linear-gradient(
          180deg,
          var(--v-theme-primary),
          rgba(255, 255, 255, 0)
        );
      }
    }

    &__bottom {
      background: linear-gradient(0deg, clothoid-gradient(#000000));
      height: 50%;
      opacity: 0.8;
      bottom: 0;
      top: auto;

      &.no-bg-image {
        background: linear-gradient(
          0deg,
          var(--v-theme-primary),
          rgba(255, 255, 255, 0)
        );
      }
    }
  }
}

/* Text field */
.login :deep(.login__text-field .v-field) {
  border-radius: 16px;
}

:deep(.v-field--variant-outlined .v-label.v-field-label--floating) {
  background: rgb(var(--v-theme-surface)) !important;
  //padding: 0 6px;
}
/* Remove background when error + blurred */
.login :deep(
  .login__text-field 
  .v-field--error:not(.v-field--focused) 
  .v-field-label--floating
) {
  background: #ffffff !important;
  color: rgb(var(--v-theme-error));
  padding: 0 4px !important;
  z-index: 2;
  position: relative;
  display: inline-block;
  top: -0px;
  left: -4px;
}

.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__start),
.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__end) {
  border-top-width: 2px !important;
  border-bottom-width: 2px !important;
  border-color: rgb(var(--v-theme-error)) !important;
}

.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__notch::before),
.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__notch::after) {
  border-top-width: 2px !important;
  border-bottom-width: 2px !important;
  border-color: rgb(var(--v-theme-error)) !important;
}

/* When focused, hide notch border color */
.login :deep(
  .login__text-field 
  .v-field--error.v-field--focused.v-field--variant-outlined 
  .v-field__outline__notch::before
),
.login :deep(
  .login__text-field 
  .v-field--error.v-field--focused.v-field--variant-outlined 
  .v-field__outline__notch::after
) {
  border-top-color: transparent !important;
}

.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__start) {
  border-left-width: 2px !important;
}

.login :deep(.login__text-field .v-field--error.v-field--variant-outlined .v-field__outline__end) {
  border-right-width: 2px !important;
}

/* Default border */
// .login :deep(.login__text-field .v-field--variant-outlined) {
//   --v-field-border-width: 1px;
// }

/* Error border */
.login :deep(.login__text-field .v-field--error.v-field--variant-outlined) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1;
}

/* Default (not focused, not error) */
.login :deep(
  .login__text-field 
  .v-field--variant-outlined:not(.v-field--focused):not(.v-field--error)
  .v-field__outline
) {
  color: rgba(0,0,0,1) !important;
}

/* Keep full red border even after blur */
.login :deep(
  .login__text-field
  .v-field--error.v-field--variant-outlined:not(.v-field--focused)
) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1; /* This is the key */
}

// :deep(.v-field__outline) {
//   border-radius: 16px !important;
//   color: rgba(var(--v-theme-on-surface), 1) !important;
// }

//   :deep(.v-field--focused .v-field__outline) {
//     color: rgba(var(--v-theme-primary), 1) !important;
//   }

:deep(.v-field--variant-outlined .v-field__outline__start) {
  flex: 0 0 16px !important;
  border-start-start-radius: 16px !important;
  border-end-start-radius: 16px !important;
  border-top-left-radius: 16px !important;
  border-bottom-left-radius: 16px !important;
}

.login :deep(.login__text-field .v-field) {
  --v-field-padding-start: 12px;
  --v-field-padding-end: 16px;
}

.login :deep(.login__text-field .v-input__details) {
  margin-top: 0px;
  padding-top: 0px;
  padding-left: 12px;
  min-height: 18px;
  margin-bottom: 9px;
  letter-spacing: 0 !important;
}

.login__btn {
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
}
</style>
